import React, { ChangeEvent } from 'react';
import { faPalette, faFolderArrowUp } from '@fortawesome/pro-regular-svg-icons';
import { FormLabel, NotificationDispatch } from '@tryhackme/thm-ui-components';
import { yupResolver } from '@hookform/resolvers/yup';
import { useForm, useWatch } from 'src/common/form';
import { useGetRoomIconPresignedUrlMutation, useUploadIconToS3Mutation } from 'src/features/my-rooms/my-rooms.slice';
import { isQueryErrorWithMessage } from 'src/common/helpers/slices';
import { store } from 'src/app/store';
import { setIsDirty } from 'src/app/reducers/manage-room.reducer';
import { useIsDarkMode } from 'src/common/hooks/use-is-dark-mode';
import { HttpStatus } from 'src/common/enums';
import { SettingCard } from '../setting-card';
import { FormRow } from '../form-row';
import { UpdateRoomConfirmModal } from '../update-room-confirm-modal';
import { useGenerateRandomRoomAvatarMutation, useUpdateRoomDesignMutation } from '../../manage-room.slice';
import { DiscardChangesModal } from '../discard-changes-modal';
import { UnSavedWarnModal } from '../unsaved-warn-modal';
import { StyledBottomAdornment, StyledButtonContainer, StyledChooseFileContainer, StyledFileBrowseButton, StyledFileInputWrapper, StyledFilePlaceholder, StyledFileUploadIcon, StyledLocalFileName, StyledRandomAvatarButtonContainer, StyledRoomAvatarImg, StyledRoomBannerImg } from './design-tab.styles';
import { GenerateRandomAvatarConfirmModal } from './components';
import { updateRoomDesignValidations } from './design-tab.constants';
import { DesignTabProps } from './design-tab.types';
import { RoomBannerUpload } from './components/room-banner-upload';
export const DesignTab = ({
  roomDetails
}: DesignTabProps) => {
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const [generateRandomAvatar, {
    isLoading: isGeneratingRandomAvatar
  }] = useGenerateRandomRoomAvatarMutation();
  const [uploadFile] = useUploadIconToS3Mutation();
  const [getPresignedURL] = useGetRoomIconPresignedUrlMutation();
  const [updateDesign, {
    isLoading
  }] = useUpdateRoomDesignMutation();
  const [localFileName, setLocalFileName] = React.useState<string>('');
  const [openUpdateModal, setOpenUpdateModal] = React.useState<boolean>(false);
  const isDarkMode = useIsDarkMode();
  const defaultValues = {
    headerImage: roomDetails?.headerImage ?? '',
    imageURL: roomDetails?.imageURL ?? '',
    uploadedFileName: ''
  };
  const {
    register,
    formState: {
      isDirty,
      errors
    },
    reset,
    setValue,
    control,
    handleSubmit
  } = useForm({
    resolver: yupResolver(updateRoomDesignValidations),
    defaultValues,
    mode: 'onChange'
  });
  const imageURL = useWatch({
    control,
    name: 'imageURL'
  });
  React.useEffect(() => {
    store.dispatch(setIsDirty(isDirty));
  }, [isDirty]);
  const handleFileBrowse = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };
  const handleDiscardChanges = () => {
    reset();
    clearLocalFileName();
  };
  const handleGenerateRandomAvatar = async () => {
    try {
      const response = await generateRandomAvatar({
        roomCode: roomDetails?.code
      }).unwrap();
      setValue('imageURL', (response?.data?.url as string), {
        shouldDirty: true
      });
      setValue('uploadedFileName', '', {
        shouldDirty: true
      });
      setLocalFileName('');
    } catch {
      NotificationDispatch('error', 'There was problem creating random avatar. Please try again later.');
    }
  };
  const handleFileUpload = async (e: ChangeEvent<HTMLInputElement>) => {
    try {
      const fileInput = e.target.files;
      if (fileInput?.length && fileInput[0]) {
        const fileName = fileInput[0].name;
        const file = fileInput[0];
        const presignedResponse = await getPresignedURL({
          filename: fileName
        }).unwrap();
        const {
          url,
          filename: s3fileName
        } = presignedResponse?.data ?? {};
        await uploadFile({
          file,
          url: (url as string)
        }).unwrap();
        setLocalFileName(fileName ?? '');
        setValue('imageURL', URL.createObjectURL(file), {
          shouldDirty: true
        });
        setValue('uploadedFileName', (s3fileName as string), {
          shouldDirty: true
        });
      }
    } catch {
      clearLocalFileName();
      NotificationDispatch('error', 'Something went wrong while uploading file. Please try again.');
    }
  };
  const handleUpdateRoomDesign = async (values: {
    headerImage: string;
    imageURL: string;
    uploadedFileName: string;
  }) => {
    try {
      const {
        headerImage: banner,
        imageURL: avatar,
        uploadedFileName
      } = values;
      const payload = {
        banner,
        ...(!uploadedFileName && {
          avatar
        }),
        uploadedFileName
      };
      await updateDesign({
        roomCode: roomDetails?.code,
        payload
      }).unwrap();
      reset({
        headerImage: banner,
        imageURL: avatar
      });
      clearLocalFileName();
      NotificationDispatch('success', 'Room updated successfully.');
    } catch (error) {
      if (isQueryErrorWithMessage(error)) {
        if (error.status === HttpStatus.BAD_REQUEST) {
          NotificationDispatch('error', error.data.message);
        } else {
          NotificationDispatch('error', 'Uh-oh. There was an error while updating your room. Please try again.');
        }
      }
    } finally {
      setOpenUpdateModal(false);
    }
  };
  const clearLocalFileName = () => {
    setLocalFileName('');
  };
  const headerImageurl = useWatch({
    name: 'headerImage',
    control
  });
  return <>
      <SettingCard title="Design" icon={faPalette} data-sentry-element="SettingCard" data-sentry-source-file="design-tab.tsx">
        <FormRow heading="Banner" description="Select a 1920 x 300 px PNG image with a resolution of 72 DPI to use as a banner image" textSectionBody={<StyledRoomBannerImg isDarkMode={isDarkMode} src={headerImageurl} />} data-sentry-element="FormRow" data-sentry-source-file="design-tab.tsx">
          <RoomBannerUpload preselectedFile={roomDetails?.headerImage} register={register} errors={errors} setValue={setValue} data-sentry-element="RoomBannerUpload" data-sentry-source-file="design-tab.tsx" />
        </FormRow>

        <FormRow heading="Room avatar" description={`You can give your page a unique touch by selecting an icon. Don’t have one? 
            You can user our random icon generator to create one. Supported formats: SVG, JPG or PNG`} textSectionBody={<StyledRoomAvatarImg isDarkMode={isDarkMode} src={imageURL} />} showBottomLine={false} data-sentry-element="FormRow" data-sentry-source-file="design-tab.tsx">
          <FormLabel htmlFor="room-avatar" data-sentry-element="FormLabel" data-sentry-source-file="design-tab.tsx">Room avatar</FormLabel>
          <StyledFileInputWrapper data-sentry-element="StyledFileInputWrapper" data-sentry-source-file="design-tab.tsx">
            <StyledChooseFileContainer data-sentry-element="StyledChooseFileContainer" data-sentry-source-file="design-tab.tsx">
              <StyledFilePlaceholder data-sentry-element="StyledFilePlaceholder" data-sentry-source-file="design-tab.tsx">
                <StyledLocalFileName filename={localFileName} data-sentry-element="StyledLocalFileName" data-sentry-source-file="design-tab.tsx">
                  {localFileName || 'Choose a file...'}
                </StyledLocalFileName>
              </StyledFilePlaceholder>
            </StyledChooseFileContainer>

            <input data-testid="file-input" id="room-avatar" name="room-avatar" ref={fileInputRef} type="file" hidden accept="image/png, image/jpg, image/jpeg, image/svg"
          // eslint-disable-next-line @typescript-eslint/no-misused-promises
          onChange={handleFileUpload} />
            <StyledFileBrowseButton variant="outlined" color="secondary" onClick={handleFileBrowse} data-sentry-element="StyledFileBrowseButton" data-sentry-source-file="design-tab.tsx">
              <StyledFileUploadIcon icon={faFolderArrowUp} data-sentry-element="StyledFileUploadIcon" data-sentry-source-file="design-tab.tsx" />
              Browse...
            </StyledFileBrowseButton>
          </StyledFileInputWrapper>
          <StyledBottomAdornment data-sentry-element="StyledBottomAdornment" data-sentry-source-file="design-tab.tsx">Supported formats: SVG, JPG or PNG</StyledBottomAdornment>

          <StyledRandomAvatarButtonContainer data-sentry-element="StyledRandomAvatarButtonContainer" data-sentry-source-file="design-tab.tsx">
            <GenerateRandomAvatarConfirmModal handleActionButton={handleGenerateRandomAvatar} isGeneratingRandomAvatar={isGeneratingRandomAvatar} data-sentry-element="GenerateRandomAvatarConfirmModal" data-sentry-source-file="design-tab.tsx" />
          </StyledRandomAvatarButtonContainer>
        </FormRow>
      </SettingCard>
      <StyledButtonContainer data-sentry-element="StyledButtonContainer" data-sentry-source-file="design-tab.tsx">
        {isDirty && <DiscardChangesModal handleActionButton={handleDiscardChanges} />}
        <UpdateRoomConfirmModal open={openUpdateModal} openChange={setOpenUpdateModal} isRoomCodeChanged={false} isUpdatingRoom={isLoading} isUpdateButtonDisabled={!isDirty}
      // eslint-disable-next-line @typescript-eslint/no-misused-promises
      handleUpdateRoom={handleSubmit(handleUpdateRoomDesign)} data-sentry-element="UpdateRoomConfirmModal" data-sentry-source-file="design-tab.tsx" />
      </StyledButtonContainer>
      <UnSavedWarnModal handleDiscardChanges={handleDiscardChanges}
    // eslint-disable-next-line @typescript-eslint/no-misused-promises
    handleActionButton={handleSubmit(handleUpdateRoomDesign)} isSavingChanges={isLoading} data-sentry-element="UnSavedWarnModal" data-sentry-source-file="design-tab.tsx" />
    </>;
};